import type { Analysis } from '@ni/shared';

/**
 * There is now a content contract per analysis kind: `person_summary` and the
 * facet summaries read as `summary` prose, `club_fit` as `criteria` rows of
 * `{criterion, met, note}`, `conversation_notes` as three short lists
 * (`openers`, `common_ground`, `carry_lightly`), `relationship_read` as two
 * (`connections`, `angles`) beside its prose, and every kind may carry
 * `caveats`. `AnalysisContent` (`@ni/shared`) types those keys as optional and
 * still passes the rest through.
 *
 * Optional is the operative word, and it is why every reader below stays
 * defensive: the contract types what a *new* analysis writes, and the table is
 * full of rows written before it — a `club_fit` with no `met` on its criteria,
 * a `conversation_notes` with only a `summary`. Those rows are not migrated and
 * must not read as broken, so each reader takes what it finds, coerces nothing,
 * and names its own default where absence has a meaning (an unmarked criterion
 * is met; a note with no lists falls back to its paragraph).
 */

type Content = Record<string, unknown>;

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** A list of sentences, with anything that is not one dropped rather than cast. */
function asStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const lines: string[] = [];
  for (const item of value) {
    const line = asString(item);
    if (line) lines.push(line);
  }
  return lines;
}

/** The paragraph an analysis reads as. */
export function analysisProse(analysis: Analysis | undefined): string | null {
  if (!analysis) return null;
  return asString((analysis.content as Content)['summary']);
}

/**
 * The confidence and provenance hedging, which the prose no longer carries
 * (docs/notes/2026-08-31-first-prod-runs.md §2). Contracted rather than guessed —
 * `AnalysisContent` validates it at ingest — so this reads the field and only
 * guards against an analysis written before it existed.
 */
export function analysisCaveats(analysis: Analysis | undefined): string[] {
  return analysis?.content.caveats ?? [];
}

export interface FitRow {
  criterion: string;
  /**
   * Whether the record actually meets the value. Absent on every analysis
   * written before the contract, and those rows all read as met — which is what
   * they were: the criteria list was a list of values the subject satisfied,
   * and back-reading them as unmet would invent a tension nobody assessed.
   */
  met: boolean;
  note: string | null;
}

/** `club_fit` reads against the club's values, one row per criterion. */
export function clubFitRows(analysis: Analysis | undefined): FitRow[] {
  return criteriaRows(analysis);
}

/**
 * `wealth_fit` reads against the office's own paragraph — who it is and who it
 * serves — one row per criterion.
 *
 * The same reader as `clubFitRows` under this vertical's name, and named rather
 * than shared by accident: `criteria` is one contracted key (`AnalysisCriterion`
 * in `@ni/shared`), both kinds write it the same way, and the two blocks that
 * render it are the same grid. A second parser would have been a second place
 * for "a missing `met` reads as met" to drift. What is *not* shared is the copy
 * around the rows — the club's says "assessed against the club's values" and the
 * wealth block's says what the office wrote — because that is the part that
 * differs.
 */
export function wealthFitRows(analysis: Analysis | undefined): FitRow[] {
  return criteriaRows(analysis);
}

/** The one `criteria` reader, behind both names above. */
function criteriaRows(analysis: Analysis | undefined): FitRow[] {
  if (!analysis) return [];
  const list = (analysis.content as Content)['criteria'];
  if (!Array.isArray(list)) return [];

  const rows: FitRow[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Content;
    const criterion = asString(row['criterion']);
    const met = row['met'];
    if (criterion) {
      rows.push({
        criterion,
        met: typeof met === 'boolean' ? met : true,
        note: asString(row['note']),
      });
    }
  }
  return rows;
}

export interface ConversationLists {
  openers: string[];
  common_ground: string[];
  carry_lightly: string[];
}

/**
 * `conversation_notes` as the three lists it is: what to open with, what is
 * already shared, and what to keep off the table. One sentence per item.
 *
 * All three empty is the honest reading of an analysis written before the
 * contract — it has only a paragraph — and the block falls back to that
 * paragraph rather than printing three empty heads.
 */
export function conversationLists(analysis: Analysis | undefined): ConversationLists {
  const content = (analysis?.content ?? {}) as Content;
  return {
    openers: asStrings(content['openers']),
    common_ground: asStrings(content['common_ground']),
    carry_lightly: asStrings(content['carry_lightly']),
  };
}

export interface RelationshipLists {
  connections: string[];
  angles: string[];
}

/**
 * `relationship_read` as the two lists it is: the concrete links between the
 * person and the organisation, and what each side plausibly wants out of the
 * other.
 *
 * Both empty is a legitimate reading — a `relationship_read` that found nothing
 * to list and said so in its prose — so this answers empty arrays rather than
 * null, and the block prints the paragraph and no heads. Same defensiveness as
 * the readers above: the keys are contracted, and a row written by an engine
 * that carried only `summary` must not read as broken.
 */
export function relationshipLists(analysis: Analysis | undefined): RelationshipLists {
  const content = (analysis?.content ?? {}) as Content;
  return {
    connections: asStrings(content['connections']),
    angles: asStrings(content['angles']),
  };
}

/** The current analysis of a kind, if the record or brief carries one. */
export function analysisOfKind(analyses: Analysis[], kind: string): Analysis | undefined {
  return (
    analyses.find((analysis) => analysis.kind === kind && analysis.status === 'current') ??
    analyses.find((analysis) => analysis.kind === kind)
  );
}
